/*
 * Copyright (c) 2022, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React from 'react';
import { Box, Divider, Link, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { TooltipProps } from '@material-ui/core/Tooltip';
import Tooltip from './Tooltip';
import { useStyles, useToolTipBaseStyles } from './styles';

interface InfoTooltipProps extends TooltipProps {
  darken?: boolean;
  isDisabled?: boolean;
  heading?: string;
  content?: string;
  example?: string;
  action?: { link: string; text: string };
}

export default function InfoTooltip(props: Partial<InfoTooltipProps>) {
  const {
    children,
    heading,
    isDisabled,
    content,
    example,
    action,
    darken,
    ...restProps
  } = props;
  const classes = useStyles();
  const baseClasses = useToolTipBaseStyles();

  const infoTooltipFragment = (
    <Box p={1}>
      {heading && (
        <Box mb={content ? 1 : 0}>
          <Typography variant="h5">{heading}</Typography>
        </Box>
      )}
      {content && <Typography variant="body2">{content}</Typography>}
      {(action || example) && (
        <Divider
          className={clsx({
            [classes.divider]: true,
            [classes.dividerDark]: darken,
            [classes.dividerLight]: !darken,
          })}
        />
      )}
      {example && (
        <Typography
          className={clsx({
            [classes.exampleContent]: true,
            [classes.exampleContentDark]: darken,
            [classes.exampleContentLight]: !darken,
          })}
          variant="h4"
        >
          Eg: {example}
        </Typography>
      )}
      {action && (
        <Link
          href={action.link}
          className={classes.buttonLink}
          target="_blank"
          rel="noreferrer"
        >
          {action.text}
        </Link>
      )}
    </Box>
  );

  return (
    <>
      {children && (
        <Tooltip
          title={infoTooltipFragment}
          disabled={isDisabled}
          classes={
            darken
              ? {
                  tooltip: baseClasses.infoTooltipDark,
                  arrow: baseClasses.infoArrowDark,
                }
              : {
                  tooltip: baseClasses.infoTooltipLight,
                  arrow: baseClasses.infoArrowLight,
                }
          }
          /* eslint-disable-next-line react/jsx-props-no-spreading */
          {...restProps}
        >
          {children}
        </Tooltip>
      )}
    </>
  );
}
